import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import {
  appendProposalStructure,
  loadProposalStructures,
  type StructureRef,
} from '../../utils/proposalStructureApi';
import type { SubtaskStructureRef } from '../../utils/subtaskStructures';
import './StructureMultiSelect.css';

interface StructureMultiSelectProps {
  dealId?: string | null;
  value: SubtaskStructureRef[];
  onChange: (structures: SubtaskStructureRef[]) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** When false, only link existing proposal structures (no inline create). */
  allowCreate?: boolean;
}

const StructureMultiSelect: React.FC<StructureMultiSelectProps> = ({
  dealId,
  value,
  onChange,
  disabled = false,
  className = '',
  placeholder = 'Structures…',
  allowCreate = false,
}) => {
  const [options, setOptions] = useState<StructureRef[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (!dealId) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      setOptions(await loadProposalStructures(dealId));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!dealId) {
    return <span className="pm-structure-select-muted">No deal linked</span>;
  }

  const selectedIds = new Set(value.map((v) => v.id));

  const toggle = (ref: StructureRef) => {
    if (selectedIds.has(ref.id)) {
      onChange(value.filter((v) => v.id !== ref.id));
    } else {
      onChange([...value, ref]);
    }
  };

  const handleCreate = async () => {
    const name = window.prompt('New structure name');
    if (!name?.trim() || !dealId) return;
    setCreating(true);
    try {
      const created = await appendProposalStructure(dealId, name.trim());
      setOptions((prev) => [...prev, created]);
      if (!selectedIds.has(created.id)) {
        onChange([...value, created]);
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not create structure.');
    } finally {
      setCreating(false);
    }
  };

  const triggerLabel =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? value[0].name
        : `${value[0].name} +${value.length - 1}`;

  const triggerTitle = value.length > 1 ? value.map((v) => v.name).join(', ') : undefined;

  return (
    <div className={`pm-structure-multi ${open ? 'pm-structure-multi--open' : ''} ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`pm-structure-multi-trigger ${value.length ? 'has-value' : ''}`}
        disabled={disabled || loading || creating}
        title={triggerTitle}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="pm-structure-multi-trigger-label">{triggerLabel}</span>
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <div className="pm-structure-multi-menu" role="listbox" aria-multiselectable>
          <div className="pm-structure-multi-options">
            {options.length === 0 && !loading && (
              <div className="pm-structure-multi-empty">
                {allowCreate
                  ? 'No structures on this proposal yet.'
                  : 'No proposal structures yet. Add them in the Proposal Builder, then link them here.'}
              </div>
            )}
            {options.map((opt) => (
              <label key={opt.id} className="pm-structure-multi-option">
                <input
                  type="checkbox"
                  checked={selectedIds.has(opt.id)}
                  onChange={() => toggle(opt)}
                />
                <span>{opt.name}</span>
              </label>
            ))}
          </div>
          {allowCreate ? (
            <button
              type="button"
              className="pm-structure-multi-create"
              disabled={creating}
              onClick={() => void handleCreate()}
            >
              <Plus size={12} /> Add to proposal…
            </button>
          ) : options.length === 0 && !loading ? (
            <p className="pm-structure-multi-hint">
              Structures are defined in the Proposal Builder. Link them to this subtask here.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default StructureMultiSelect;
